"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import useHash from "@packages/hooks/DetectHashChange";
import {
  RightBlueArrowIcon,
  LeftBlueArrowIcon,
} from "@packages/lib/utlils/media-utilities/mediautilities";
import {
  GA4DataLayer,
  GA4DataLayerFn,
  replaceSpaceWithUnderscoreAndHyphen,
} from "@packages/lib/utlils/helper-function";
import {
  DataLayerGA4AttrType,
  GA4EventAttrType,
} from "@packages/lib/types/datalayerGA";
import useIsMobile from "@packages/hooks/DetectMobileDevice";
import { GAConstants } from "@packages/shared-components/services/constants";

interface fragment {
  sectionUrl: string;
  sectionName: string;
}

interface FragmentNextPreviousButtonsProps {
  fragmentList: fragment[];
  commonGAAttrs: any;
  commonOldGAAttrs:DataLayerGA4AttrType
}

const FragmentNextPreviousButtons = ({
  fragmentList,
  commonGAAttrs,
  commonOldGAAttrs
}: FragmentNextPreviousButtonsProps) => {
  const [activeHash] = useHash();
  const [activeFragmentIdx, setActiveFragmentIdx] = useState(0);
  const [isMobile] = useIsMobile();

  useEffect(() => {
    if (activeHash)
      setActiveFragmentIdx(() =>
        fragmentList?.findIndex(
          (fragment) => fragment.sectionUrl === activeHash,
        ),
      );
  }, [activeHash]);

  const startingFragmentIdx = 0;
  const endingFragmentIdx = fragmentList?.length - 1;

  function next(e: any) {
    if (activeFragmentIdx === endingFragmentIdx) return;
    const newIdx = activeFragmentIdx + 1;
    scrollIntoView(newIdx);
    e.preventDefault();
  }

  function previous(e: any) {
    if (activeFragmentIdx === 0) return;
    const newIdx = activeFragmentIdx - 1;
    scrollIntoView(newIdx);
    e.preventDefault();
    // GA4DataLayerFn(handleGALogs(fragmentList[newIdx]?.sectionName, fragmentList[activeFragmentIdx + 1]?.sectionUrl, "Previous"));
  }

  function scrollIntoView(newIdx: number) {
    setActiveFragmentIdx(1);
    window.location.hash = `#${fragmentList[newIdx]?.sectionUrl}`;
    window.scrollTo({ top: isMobile ? 550 : 470, behavior: "smooth" });
    sessionStorage?.setItem("activeSkipLink", "Y");
    setTimeout(() => {
      sessionStorage?.removeItem("activeSkipLink");
    }, 100);
  }

  const handleGALogs = (
    sectionName: string,
    ctaName: string,
  ): GA4EventAttrType => {
    return {
      event: GAConstants.track_event,
      event_name: "skip_links",
      ...commonGAAttrs,
      click_text:
        replaceSpaceWithUnderscoreAndHyphen(ctaName + "_" + sectionName) ||
        "NA",
    };
  };
  const handleOldGALogs = (sectionName: string, sectionUrl: string, ctaName: string): DataLayerGA4AttrType => {
    return {
      event: "ga_events",
      eventName: "ip_page_skiplinks",
      ...commonOldGAAttrs,
      data_label: sectionName || "NA",
      data_label2: ctaName?.toLowerCase() || "NA",
      cta_name: ctaName || "NA",
      cta_url: sectionUrl || "NA",
    }
  };

  if (!fragmentList?.length || fragmentList.length <= 1) return <></>;

  const hasPrevious =
    activeFragmentIdx > 0 &&
    activeHash !== fragmentList[startingFragmentIdx]?.sectionUrl;
  const hasNext =
    activeFragmentIdx < endingFragmentIdx &&
    activeHash !== fragmentList[endingFragmentIdx]?.sectionUrl;

  return (
    <div className="flex items-stretch justify-end gap-[8px] px-[16px] md:px-[20px] lg:px-0">
      {hasPrevious && (
        <Link
          className="mr-auto group w-1/2 text-primary-400 flex gap-[4px] items-center justify-start bg-white md:hover:bg-primary-400 md:hover:text-white border border-primary-400 rounded-[4px] p-[12px]"
          href={`#${fragmentList[activeFragmentIdx - 1]?.sectionUrl}`}
          onClick={(e) => {
            previous(e);
            GA4DataLayerFn(handleOldGALogs(fragmentList[activeFragmentIdx - 1]?.sectionName, fragmentList[activeFragmentIdx - 1]?.sectionUrl, "Previous"));
            GA4DataLayer(
              handleGALogs(
                fragmentList[activeFragmentIdx - 1]?.sectionName,
                "Previous",
              ),
            );
          }}
        >
          <span className="flex items-center justify-center w-[24px] h-[24px]">
            <LeftBlueArrowIcon hover={"md:group-hover:stroke-white"} />
          </span>
          <span className="flex flex-col text-left items-start">
            <span className="para font-semibold">Previous</span>
            <span className="small">
              {fragmentList[activeFragmentIdx - 1]?.sectionName}
            </span>
          </span>
        </Link>
      )}
      {hasNext && (
        <Link
          className="group w-1/2 text-primary-400 flex gap-[4px] items-center justify-between bg-white md:hover:bg-primary-400 md:hover:text-white border border-primary-400 rounded-[4px] p-[12px]"
          href={`#${fragmentList[activeFragmentIdx + 1]?.sectionUrl}`}
          onClick={(e) => {
            next(e);
            GA4DataLayerFn(handleOldGALogs(fragmentList[activeFragmentIdx + 1]?.sectionName, fragmentList[activeFragmentIdx + 1]?.sectionUrl, "Next"));
            GA4DataLayer(
              handleGALogs(
                fragmentList[activeFragmentIdx + 1]?.sectionName,
                "Next",
              ),
            );
          }}
        >
          <span className="flex flex-col text-left items-start">
            <span className="para font-semibold">Next</span>
            <span className="small">
              {fragmentList[activeFragmentIdx + 1]?.sectionName}
            </span>
          </span>
          <span className="flex items-center justify-center w-[24px] h-[24px]">
            <RightBlueArrowIcon hover={"md:group-hover:stroke-white"} />
          </span>
        </Link>
      )}
    </div>
  );
};

export default FragmentNextPreviousButtons;
